import type {
  ChangeEvent,
  Dispatch,
  MutableRefObject,
  PropsWithChildren,
  ReactNode,
  Ref,
} from 'react'
import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react'
import { useSyncRefs } from '../hooks/useSyncRefs'
import Menu from '../Menu/Menu'
import type SvgIcon from '../SvgIcon/SvgIcon'
import type {
  TextInputColor,
  TextInputSize,
  TextInputVariant,
} from '../TextInput/styles'
import type { Actions } from './Actions'
import { ActionTypes } from './Actions'
import Group from './Group'
import type { OptionProps } from './Option'
import Option from './Option'
import Text from './Text'

type EventActions = 'revert'

type SelectReducers = {
  [ActionType in ActionTypes]: (
    state: SelectState,
    action: Actions,
  ) => SelectState
}

interface SelectState {
  value: string
}

type SelectContextState = SelectState & {
  displayInputRef: MutableRefObject<HTMLInputElement | null>
  inputRef: MutableRefObject<HTMLInputElement | null>
}

type UseSelectValues = [SelectContextState, Dispatch<Actions>]

const SelectContext = createContext<
  [SelectContextState, Dispatch<Actions>] | null
>(null)

const reducers: SelectReducers = {
  [ActionTypes.SelectOption]: (
    state,
    action: Extract<Actions, { type: ActionTypes.SelectOption }>,
  ) => ({ ...state, value: action.value }),
}

const runReducer = (state: SelectState, action: Actions): SelectState =>
  reducers[action.type](state, action)

export const useSelect = (): UseSelectValues =>
  useContext(SelectContext) as UseSelectValues

interface SelectProps {
  color?: TextInputColor
  defaultValue?: string
  dataSet?: Record<string, string | undefined>
  disabled?: boolean
  error?: boolean
  helperText?: string
  inputRef?: Ref<HTMLInputElement>
  label: string
  name?: string
  onChange?: (
    event: ChangeEvent<HTMLInputElement>,
    actions: Record<EventActions, () => void>,
  ) => void
  size?: TextInputSize
  startAdornment?: typeof SvgIcon | ReactNode
  variant?: TextInputVariant
}

function Select({
  children,
  color = 'default',
  dataSet,
  defaultValue = '',
  disabled = false,
  error = false,
  helperText,
  inputRef,
  label,
  name,
  onChange,
  size = 'medium',
  startAdornment,
  variant = 'outlined',
}: PropsWithChildren<SelectProps>): JSX.Element {
  const displayInputRef = useRef<HTMLInputElement>(null)
  const localRef = useRef<HTMLInputElement>(null)
  const syncRef = useSyncRefs<HTMLInputElement | null>(localRef, inputRef)

  const [state, dispatch] = useReducer(runReducer, {}, () => ({
    value: defaultValue,
  }))

  useEffect(() => {
    // When options do not contain an empty option, display value should be adjusted manually
    if (state.value === '' && displayInputRef.current) {
      displayInputRef.current.value = ''
    }
  }, [state.value])

  const revert = () => {
    const currentState = { ...state }

    return () => {
      const input = localRef.current

      setTimeout(() => {
        dispatch({ type: ActionTypes.SelectOption, ...currentState })
      }, 1)

      if (input) {
        input.value = currentState.value
      }
    }
  }

  return (
    <SelectContext.Provider
      value={[{ ...state, displayInputRef, inputRef: localRef }, dispatch]}
    >
      <Menu>
        <Text
          color={color}
          dataSet={dataSet}
          defaultValue={defaultValue}
          disabled={disabled}
          displayInputRef={displayInputRef}
          error={error}
          helperText={helperText}
          inputRef={syncRef}
          label={label}
          name={name}
          onChange={onChange}
          revert={revert}
          size={size}
          startAdornment={startAdornment}
          variant={variant}
        />
        <Menu.Items>{children}</Menu.Items>
      </Menu>
    </SelectContext.Provider>
  )
}

Select.Option = forwardRef<HTMLButtonElement, PropsWithChildren<OptionProps>>(
  Option,
)
Select.Group = Group

export default Select
